import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import {
    Baseline, Bold, Check, ChevronDown, Code, Highlighter, Italic, Link, List, ListOrdered, MoreHorizontal, Quote,
    Strikethrough, Trash2, Type, Underline
} from "@fg/icons";
import { COLORS, FONTS, HIGHLIGHTS, OFFERED_FONTS, SIZES } from "@/lib/richFormat";
import { cn } from "@/lib/utils";
import type { ActiveFormats, RichCommand, SimpleCommandType } from "./RichEditor";

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
const MOD = isMac ? "⌘" : "Ctrl+";
const SHIFT = isMac ? "⇧" : "Shift+";

const BUTTON = "flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors " +
    "hover:bg-accent hover:text-foreground active:bg-accent/70 " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-40";
const PRESSED = "bg-primary/15 text-primary hover:bg-primary/20 hover:text-primary";

/** A toolbar button: keeps the editor's selection (mousedown is not allowed to take focus), shows a pressed state. */
export function ToolButton({ label, shortcut, pressed, disabled, onClick, children, className }: {
    label: string;
    shortcut?: string;
    pressed?: boolean;
    disabled?: boolean;
    onClick: () => void;
    children: ReactNode;
    className?: string;
}) {
    return (
        <button
            type="button"
            aria-label={label}
            aria-pressed={pressed === undefined ? undefined : pressed}
            title={shortcut ? `${label} (${shortcut})` : label}
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={onClick}
            className={cn(BUTTON, pressed && PRESSED, className)}
        >
            {children}
        </button>
    );
}

/** A button that opens a small menu under it. Items are buttons (arrow keys move between them, Escape closes). */
function ToolMenu({ label, shortcut, pressed, icon, indicator, children, align = "left", disabled, text }: {
    label: string;
    shortcut?: string;
    pressed?: boolean;
    icon: ReactNode;
    /** A colour bar under the icon (font colour, highlight). */
    indicator?: string | null;
    children: (close: () => void) => ReactNode;
    align?: "left" | "right";
    disabled?: boolean;
    /** A name shown in the button instead of an icon (the current font). */
    text?: string;
}) {
    const [open, setOpen] = useState(false);
    const wrap = useRef<HTMLDivElement>(null);
    const trigger = useRef<HTMLButtonElement>(null);
    // The composer sits at the bottom of the window: when the menu would not fit below the button it opens upwards.
    const [up, setUp] = useState(false);
    useEffect(() => {
        if (!open || !trigger.current) return;
        const r = trigger.current.getBoundingClientRect();
        setUp(window.innerHeight - r.bottom < 300 && r.top > window.innerHeight - r.bottom);
    }, [open]);
    const menuId = useId();

    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false); };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [open]);

    const items = () => Array.from(wrap.current?.querySelectorAll<HTMLElement>('[role="menuitem"],[role="menuitemradio"]') ?? []);
    const focusItem = (delta: number, from?: Element | null) => {
        const list = items();
        if (list.length === 0) return;
        const i = from ? list.indexOf(from as HTMLElement) : -1;
        list[(i + delta + list.length) % list.length]?.focus();
    };

    return (
        <div
            ref={wrap}
            className="relative"
            onKeyDown={(e) => {
                if (e.key === "Escape" && open) { e.preventDefault(); e.stopPropagation(); setOpen(false); trigger.current?.focus(); }
                else if (open && (e.key === "ArrowDown" || e.key === "ArrowRight")) { e.preventDefault(); focusItem(1, document.activeElement); }
                else if (open && (e.key === "ArrowUp" || e.key === "ArrowLeft")) { e.preventDefault(); focusItem(-1, document.activeElement); }
                else if (!open && e.key === "ArrowDown" && document.activeElement === trigger.current) {
                    e.preventDefault();
                    setOpen(true);
                    window.requestAnimationFrame(() => items()[0]?.focus());
                }
            }}
        >
            <button
                ref={trigger}
                type="button"
                aria-label={label}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls={open ? menuId : undefined}
                aria-pressed={pressed === undefined ? undefined : pressed}
                title={shortcut ? `${label} (${shortcut})` : label}
                disabled={disabled}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setOpen((o) => !o)}
                className={cn(BUTTON, "relative gap-0", text ? "w-[7.5rem] justify-between px-2" : "w-9", (pressed || open) && PRESSED)}
            >
                {text ? <span className="min-w-0 truncate text-xs">{text}</span> : icon}
                <ChevronDown className="h-2.5 w-2.5 opacity-70" aria-hidden />
                {indicator ? <span aria-hidden className="absolute bottom-1 left-1.5 h-[3px] w-4 rounded-full" style={{ backgroundColor: indicator }} /> : null}
            </button>
            {open ? (
                <div
                    id={menuId}
                    role="menu"
                    aria-label={label}
                    className={cn(
                        up ? "absolute bottom-full z-40 mb-1 min-w-[10rem]" : "absolute top-full z-40 mt-1 min-w-[10rem]",
                        "rounded-md border bg-popover p-1 text-sm text-popover-foreground shadow-lg",
                        align === "right" ? "right-0" : "left-0"
                    )}
                >
                    {children(() => setOpen(false))}
                </div>
            ) : null}
        </div>
    );
}

const ITEM = "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left hover:bg-accent focus-visible:bg-accent focus-visible:outline-none disabled:pointer-events-none disabled:opacity-40";

function MenuItem({ children, onClick, disabled, checked, role = "menuitem" }: {
    children: ReactNode;
    onClick: () => void;
    disabled?: boolean;
    checked?: boolean;
    role?: "menuitem" | "menuitemradio";
}) {
    return (
        <button
            type="button"
            role={role}
            aria-checked={role === "menuitemradio" ? Boolean(checked) : undefined}
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={onClick}
            className={cn(ITEM, checked && "bg-accent/60")}
        >
            {children}
        </button>
    );
}

function Swatches({ items, current, onPick, noneLabel, onNone, kind }: {
    items: { id: string; hex: string; label: string }[];
    current: string | null;
    onPick: (id: string) => void;
    noneLabel: string;
    onNone: () => void;
    kind: string;
}) {
    return (
        <div className="w-[11.5rem]">
            <div className="grid grid-cols-5 gap-1 p-1" role="group" aria-label={kind}>
                {items.map((c) => (
                    <button
                        key={c.id}
                        type="button"
                        role="menuitemradio"
                        aria-checked={current === c.id}
                        aria-label={c.label}
                        title={c.label}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => onPick(c.id)}
                        className="relative flex h-8 w-8 items-center justify-center rounded-md border border-black/10 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:border-white/20"
                        style={{ backgroundColor: c.hex }}
                    >
                        {current === c.id ? <Check className="h-4 w-4 text-white mix-blend-difference" aria-hidden /> : null}
                    </button>
                ))}
            </div>
            <MenuItem onClick={onNone}>{noneLabel}</MenuItem>
        </div>
    );
}

const hexOf = (list: { id: string; hex: string }[], id: string | null) => list.find((c) => c.id === id)?.hex ?? null;

const SEPARATOR = <span aria-hidden className="mx-1 h-5 w-px shrink-0 bg-border" />;

/**
 * The formatting row of the rich composer: Bold, Italic, Underline, Strikethrough | lists |
 * Font, Highlight, Font colour, Font size | Quote, Link, Code | More ... and, at the far right, Discard.
 * Used by the chat, channel and reply composers and by the edit-message box.
 */
export function RichToolbar({ formats, disabled, onRun, onLink, onDiscard }: {
    formats: ActiveFormats;
    disabled?: boolean;
    onRun: (cmd: RichCommand) => void;
    onLink: () => void;
    onDiscard?: () => void;
}) {
    const run = (type: SimpleCommandType) => () => onRun({ type });
    return (
        <div role="toolbar" aria-label="Formatting" aria-orientation="horizontal" className="flex flex-wrap items-center gap-0.5 border-b px-1 py-1">
            <ToolButton label="Bold" shortcut={`${MOD}B`} pressed={formats.bold} disabled={disabled} onClick={run("bold")}><Bold className="h-4 w-4" /></ToolButton>
            <ToolButton label="Italic" shortcut={`${MOD}I`} pressed={formats.italic} disabled={disabled} onClick={run("italic")}><Italic className="h-4 w-4" /></ToolButton>
            <ToolButton label="Underline" shortcut={`${MOD}U`} pressed={formats.underline} disabled={disabled} onClick={run("underline")}><Underline className="h-4 w-4" /></ToolButton>
            <ToolButton label="Strikethrough" shortcut={`${MOD}${SHIFT}5`} pressed={formats.strike} disabled={disabled} onClick={run("strike")}><Strikethrough className="h-4 w-4" /></ToolButton>
            {SEPARATOR}
            <ToolButton label="Bulleted list" shortcut={`${MOD}${SHIFT}8`} pressed={formats.ul} disabled={disabled} onClick={run("ul")}><List className="h-4 w-4" /></ToolButton>
            <ToolButton label="Numbered list" shortcut={`${MOD}${SHIFT}7`} pressed={formats.ol} disabled={disabled} onClick={run("ol")}><ListOrdered className="h-4 w-4" /></ToolButton>
            {SEPARATOR}
            <ToolMenu label="Font" pressed={formats.font !== null} disabled={disabled} icon={null} text={FONTS.find((f) => f.id === formats.font)?.label ?? "Default"}>
                {(close) => (
                    <div className="max-h-72 w-48 overflow-y-auto">
                        <MenuItem role="menuitemradio" checked={formats.font === null} onClick={() => { onRun({ type: "font", id: null }); close(); }}>
                            <span>Default</span>
                            {formats.font === null ? <Check className="ml-auto h-4 w-4" aria-hidden /> : null}
                        </MenuItem>
                        {OFFERED_FONTS.map((f) => (
                            <MenuItem key={f.id} role="menuitemradio" checked={formats.font === f.id} onClick={() => { onRun({ type: "font", id: f.id }); close(); }}>
                                <span style={{ fontFamily: f.stack }}>{f.label}</span>
                                {formats.font === f.id ? <Check className="ml-auto h-4 w-4" aria-hidden /> : null}
                            </MenuItem>
                        ))}
                    </div>
                )}
            </ToolMenu>
            <ToolMenu label="Highlight" pressed={formats.highlight !== null} disabled={disabled} icon={<Highlighter className="h-4 w-4" />} indicator={hexOf(HIGHLIGHTS, formats.highlight)}>
                {(close) => (
                    <Swatches
                        kind="Highlight colours"
                        items={HIGHLIGHTS}
                        current={formats.highlight}
                        onPick={(id) => { onRun({ type: "highlight", id }); close(); }}
                        noneLabel="No colour"
                        onNone={() => { onRun({ type: "highlight", id: null }); close(); }}
                    />
                )}
            </ToolMenu>
            <ToolMenu label="Font colour" pressed={formats.color !== null} disabled={disabled} icon={<Baseline className="h-4 w-4" />} indicator={hexOf(COLORS, formats.color)}>
                {(close) => (
                    <Swatches
                        kind="Font colours"
                        items={COLORS}
                        current={formats.color}
                        onPick={(id) => { onRun({ type: "color", id }); close(); }}
                        noneLabel="Automatic"
                        onNone={() => { onRun({ type: "color", id: null }); close(); }}
                    />
                )}
            </ToolMenu>
            <ToolMenu label="Font size" pressed={formats.size !== null} disabled={disabled} icon={<Type className="h-4 w-4" />}>
                {(close) => (
                    <div className="w-36">
                        {[{ id: null as string | null, label: "Normal", px: 14 }, ...SIZES.map((s) => ({ id: s.id as string | null, label: s.label, px: s.px }))].map((s) => (
                            <MenuItem key={s.id ?? "normal"} role="menuitemradio" checked={formats.size === s.id} onClick={() => { onRun({ type: "size", id: s.id }); close(); }}>
                                <span style={{ fontSize: s.px }} className="leading-tight">{s.label}</span>
                                {formats.size === s.id ? <Check className="ml-auto h-4 w-4" aria-hidden /> : null}
                            </MenuItem>
                        ))}
                    </div>
                )}
            </ToolMenu>
            {SEPARATOR}
            <ToolButton label="Quote" shortcut={`${MOD}${SHIFT}9`} pressed={formats.quote} disabled={disabled} onClick={run("quote")}><Quote className="h-4 w-4" /></ToolButton>
            <ToolButton label="Link" shortcut={`${MOD}K`} pressed={formats.link} disabled={disabled} onClick={onLink}><Link className="h-4 w-4" /></ToolButton>
            <ToolMenu label="Code snippet" shortcut={`${MOD}${SHIFT}C`} pressed={formats.code || formats.codeBlock} disabled={disabled} icon={<Code className="h-4 w-4" />}>
                {(close) => (
                    <div className="w-40">
                        <MenuItem role="menuitemradio" checked={formats.code} onClick={() => { onRun({ type: "code-inline" }); close(); }}>Inline code</MenuItem>
                        <MenuItem role="menuitemradio" checked={formats.codeBlock} onClick={() => { onRun({ type: "code-block" }); close(); }}>Code block</MenuItem>
                    </div>
                )}
            </ToolMenu>
            <ToolMenu label="More options" disabled={disabled} icon={<MoreHorizontal className="h-4 w-4" />} align="right">
                {(close) => (
                    <div className="w-52">
                        <MenuItem onClick={() => { onRun({ type: "clear" }); close(); }}>Clear formatting</MenuItem>
                        <MenuItem onClick={() => { onRun({ type: "hr" }); close(); }}>Horizontal rule</MenuItem>
                        <MenuItem disabled={!formats.inList} onClick={() => { onRun({ type: "outdent" }); close(); }}>Decrease indent</MenuItem>
                        <MenuItem disabled={!formats.inList} onClick={() => { onRun({ type: "indent" }); close(); }}>Increase indent</MenuItem>
                        <MenuItem disabled={!formats.link} onClick={() => { onRun({ type: "unlink" }); close(); }}>Remove link</MenuItem>
                        <MenuItem onClick={() => { onRun({ type: "undo" }); close(); }}>Undo <span className="ml-auto text-[11px] text-muted-foreground">{MOD}Z</span></MenuItem>
                        <MenuItem onClick={() => { onRun({ type: "redo" }); close(); }}>Redo <span className="ml-auto text-[11px] text-muted-foreground">{MOD}Y</span></MenuItem>
                    </div>
                )}
            </ToolMenu>
            {onDiscard ? (
                <ToolButton label="Discard" disabled={disabled} onClick={onDiscard} className="ml-auto hover:text-destructive">
                    <Trash2 className="h-4 w-4" />
                </ToolButton>
            ) : null}
        </div>
    );
}
